'use client'

import { useState } from 'react'
import { toast } from 'sonner'
import { Icon } from './icon'
import type { CartaoPayload } from '@/lib/export/types'

export type ExportFormat = 'pdf' | 'png'

interface SoftExportBarProps {
  /**
   * Modo legado: só notifica o clique (abre modal). Preferir `buildCartao`
   * para download direto no device.
   */
  onExport?: (format: ExportFormat) => void
  /** Constrói o payload e baixa PDF/imagem direto no navegador. */
  buildCartao?: () => CartaoPayload
  label?: string
}

/**
 * Atalho de exportação no resultado.
 * Com `buildCartao`: download automático via blob (sem modal).
 * Sem `buildCartao`: chama `onExport` (compat com ExportModal).
 */
export function SoftExportBar({
  onExport,
  buildCartao,
  label = 'Baixar resultado:',
}: SoftExportBarProps) {
  const [busy, setBusy] = useState<ExportFormat | null>(null)

  function baixar(blob: Blob, nome: string, ext: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `calculamei-${nome}.${ext}`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 15000)
  }

  async function exportDirect(format: ExportFormat) {
    if (!buildCartao || busy) return
    const payload = buildCartao()
    setBusy(format)
    try {
      const res = await fetch(`/api/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      // Falha nunca pode ser silenciosa: antes o botão simplesmente não fazia
      // nada e a pessoa clicava de novo achando que era o celular dela.
      if (!res.ok) {
        toast.error('Não consegui gerar o arquivo. Tenta de novo em instantes.')
        return
      }
      baixar(await res.blob(), payload.nome, format)
    } catch {
      toast.error('Sem conexão pra gerar o arquivo. Verifica a internet e tenta de novo.')
    } finally {
      setBusy(null)
    }
  }

  function handle(format: ExportFormat) {
    if (buildCartao) {
      void exportDirect(format)
      return
    }
    onExport?.(format)
  }

  return (
    <>
      <div className="mt-4 flex items-center gap-2.5">
        <span className="whitespace-nowrap text-[11.5px] text-ink-mute">{label}</span>
        <div className="flex flex-1 gap-1.5">
          <SoftButton
            icon="download"
            label={busy === 'pdf' ? 'Baixando…' : 'PDF'}
            disabled={busy !== null}
            onClick={() => handle('pdf')}
          />
          <SoftButton
            icon="image"
            label={busy === 'png' ? 'Baixando…' : 'Imagem'}
            disabled={busy !== null}
            onClick={() => handle('png')}
          />
        </div>
      </div>
    </>
  )
}

function SoftButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: 'download' | 'image'
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-9 flex-1 items-center justify-center gap-1.5 border border-line bg-surface text-ink-mute transition-colors hover:border-brand hover:bg-brand-tint hover:text-brand disabled:opacity-60"
    >
      <Icon name={icon} size={14} />
      <span className="text-[11.5px]">{label}</span>
    </button>
  )
}
